import { inject, Injectable } from '@angular/core';
import { ApiDataAccess, LocalStorageDataAccess } from '@softline/core';
import {
  SOFTLINE_API_ABC_ANALYSIS_CATEGORIES,
  SOFTLINE_API_ABC_ANALYSIS_FIELD_OK_QUERY,
  SOFTLINE_API_ABC_ANALYSIS_PRODUCTS,
  SOFTLINE_API_ABC_ANALYSIS_STORAGE_LOCATION_SCAN,
} from '../abc-analysis.api';
import { ApiQuery } from '../models/api-query';
import { Category } from '../models/category';
import { Configuration } from '../models/configuration';
import { FieldOkResult } from '../models/field-ok-result';
import { ProductWithAdditionalData } from '../models/product-with-additional-data';
import { StorageLocationScanResult } from '../models/storage-location-scan-result';

export interface IAbcAnalysisRepository {
  loadConfiguration(locationKey: string): Configuration | null;
  saveConfiguration(locationKey: string, value: Configuration): void;
  scanStorageLocation(storageLocationCode: string): Promise<StorageLocationScanResult>;
  getCategories(query: ApiQuery): Promise<Category[]>;
  getProducts(segmentId: string, query: ApiQuery): Promise<ProductWithAdditionalData[]>;
  getConfigurations(body: {
    filter: string;
    multiValued: boolean;
    parameters: object;
  }): Promise<FieldOkResult<Configuration> | undefined>;
}

@Injectable()
export class AbcAnalysisRepository implements IAbcAnalysisRepository {
  private readonly localStorageDataAccess = inject(LocalStorageDataAccess);
  private readonly dataAccess = inject(ApiDataAccess);

  loadConfiguration(locationKey: string): Configuration | null {
    return this.localStorageDataAccess.get({ key: locationKey }) ?? null;
  }

  saveConfiguration(locationKey: string, value: Configuration): void {
    this.localStorageDataAccess.set({ key: locationKey }, value);
  }

  resetConfiguration(locationKey: string): void {
    if (this.localStorageDataAccess.get({ key: locationKey }) !== null) {
      this.localStorageDataAccess.delete({ key: locationKey });
    }
  }

  async scanStorageLocation(
    storageLocationCode: string
  ): Promise<StorageLocationScanResult> {
    return await this.dataAccess.get({
      path: SOFTLINE_API_ABC_ANALYSIS_STORAGE_LOCATION_SCAN,
      pathParams: { storageLocationCode },
    });
  }

  async getCategories(query: ApiQuery): Promise<Category[]> {
    return await this.dataAccess.create<ApiQuery, Category[]>(
      { path: SOFTLINE_API_ABC_ANALYSIS_CATEGORIES },
      query
    );
  }

  async getProducts(
    segmentId: string,
    query: ApiQuery
  ): Promise<ProductWithAdditionalData[]> {
    return await this.dataAccess.create<ApiQuery, ProductWithAdditionalData[]>(
      { path: SOFTLINE_API_ABC_ANALYSIS_PRODUCTS, pathParams: { segmentId } },
      query
    );
  }

  async getConfigurations(body: {
    filter: string;
    multiValued: boolean;
    parameters: object;
  }): Promise<FieldOkResult<Configuration> | undefined> {
    return await this.dataAccess.create(
      { path: SOFTLINE_API_ABC_ANALYSIS_FIELD_OK_QUERY },
      body
    );
  }
}
